import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  UNIT_PRESETS,
  formatMoney,
  formatQuantity,
  fromUnits,
  margenBp,
  parseMoney,
  parseQuantity,
  precioParaMargen,
  unitCostFromTotal,
  ZERO_QTY,
  type Centavos,
} from '@abastece/shared';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { crearProducto, etiquetaListaPrecio, ordenarListas } from '../lib/catalogo.js';
import { emitir } from '../lib/emitir.js';
import { usePuede } from '../lib/sesion.js';
import { Campo, Selector } from '../ui/Campo.js';
import { CampoCodigo } from '../ui/CampoCodigo.js';
import { Margen } from '../ui/Margen.js';
import { Pagina } from '../ui/Pagina.js';
import { CampoCategoria } from '../ui/CampoCategoria.js';
import { Ico } from '../ui/Iconos.js';
import { useT } from '../i18n/index.js';
import { etiquetaUnidad } from '../i18n/dominio.js';

const BASES = UNIT_PRESETS.filter((u) => u.code === u.base);

interface Guardado {
  nombre: string;
  precio: Centavos | null;
  costo: Centavos | null;
  cantidad: string;
}

/**
 * Bulk entry.
 *
 * The business is starting from a paper list and somebody is about to type in several hundred
 * products. That makes this the screen that decides whether the first week is an afternoon or
 * a slog, so it is built for a keyboard on a laptop and never needs the mouse:
 *
 *   nombre → costo → existencia → precios → Enter → next row, focus back on nombre
 *
 * Tabbing out of the cost field fills both prices at the target margins, which is where most
 * of the speed comes from — the alternative is doing arithmetic several hundred times.
 *
 * With the goods in hand the row starts one field earlier, at the barcode: a USB scanner types
 * the code and presses Enter, which moves on to the name rather than saving a row with no name,
 * and the camera does the same. A row saved with a code sends the next one back to the code.
 *
 * The prices, and the cost and margins that work them out, are the owner's (2026-10-13): a cashier
 * types the code, the name, the category and the unit, and the owner prices them in «Precios».
 *
 * Opening stock is for opening day. It goes in against Capital — the owner's goods, already on
 * the shelf — with no purchase and no payment behind it. Once the shop has sold anything, a
 * product still gets made here with its prices, but its stock comes in with a purchase, which
 * says what it cost and how it was paid; otherwise goods could appear from nowhere.
 */
export function CargaRapida() {
  const t = useT();
  const listas = useLiveQuery(() => db.listas.toArray(), [], []);
  const yaVende = useLiveQuery(async () => (await db.ventas.count()) > 0, [], false);
  /** The shop's margin floor, which colours the margin as everywhere else: it was a fixed 10% here. */
  const minimoBp = useLiveQuery(async () => (await readSettings()).precioMargenMinimoBp, [], 1_000);
  /**
   * Opening stock is the owner's (`inventario.saldo_inicial`, against Capital): a cashier who
   * keeps the products since 2026-10-09 makes them here with their prices, and the stock comes in
   * with a purchase or a count.
   */
  const puedeAbrirExistencia = usePuede('configurar');
  const conExistencia = !yaVende && puedeAbrirExistencia;
  /**
   * The prices, and the cost and margins that work them out: the owner's since 2026-10-13. A cashier
   * makes the products here without them, for the owner to price.
   */
  const puedePrecios = usePuede('editar_precios');
  const activas = ordenarListas(listas);
  const publico = activas[0];
  const mayorista = activas[1];

  const [categoria, setCategoria] = useState('Abarrotes');
  const [unidadBase, setUnidadBase] = useState('unidad');
  // The shop's, the one place there is (the owner, 2026-10-05).
  const ubicacion = 'tienda';
  const [margenPublico, setMargenPublico] = useState('30');
  const [margenMayorista, setMargenMayorista] = useState('15');

  const [codigo, setCodigo] = useState('');
  const [nombre, setNombre] = useState('');
  const [costo, setCosto] = useState('');
  const [cantidad, setCantidad] = useState('');
  const [precio1, setPrecio1] = useState('');
  const [precio2, setPrecio2] = useState('');

  const [guardados, setGuardados] = useState<Guardado[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const nombreRef = useRef<HTMLInputElement>(null);
  const codigoRef = useRef<HTMLInputElement>(null);

  const costoCentavos = parseMoney(costo);
  const p1 = parseMoney(precio1);
  const p2 = parseMoney(precio2);

  /** Fill both prices from cost when the cashier leaves the cost field, if they are still blank. */
  const sugerirPrecios = useCallback(() => {
    if (costoCentavos === null || costoCentavos <= 0) return;
    const bp1 = Math.round(Number(margenPublico) * 100);
    const bp2 = Math.round(Number(margenMayorista) * 100);
    if (!precio1 && Number.isFinite(bp1)) {
      setPrecio1(String(precioParaMargen(costoCentavos, bp1) / 100));
    }
    if (!precio2 && Number.isFinite(bp2) && mayorista) {
      setPrecio2(String(precioParaMargen(costoCentavos, bp2) / 100));
    }
  }, [costoCentavos, margenPublico, margenMayorista, precio1, precio2, mayorista]);

  function limpiar(volverAlCodigo = false) {
    setCodigo('');
    setNombre('');
    setCosto('');
    setCantidad('');
    setPrecio1('');
    setPrecio2('');
    (volverAlCodigo ? codigoRef : nombreRef).current?.focus();
  }

  async function guardar() {
    if (!nombre.trim() || ocupado) return;
    setOcupado(true);
    setError(null);

    try {
      const precios = puedePrecios
        ? [
            ...(publico && p1 !== null && p1 > 0 ? [{ listaId: publico.id, precio: p1 }] : []),
            ...(mayorista && p2 !== null && p2 > 0 ? [{ listaId: mayorista.id, precio: p2 }] : []),
          ]
        : [];

      const { productoId } = await crearProducto({
        nombre,
        codigosBarras: [codigo],
        categoria,
        unidadBase,
        unidadVentaNombre: unidadBase,
        factor: fromUnits(1),
        precios,
      });

      // Opening stock, if any was typed and the shop has not opened yet. Against Capital, not an expense.
      const qty = parseQuantity(cantidad);
      if (conExistencia && qty !== null && qty > 0) {
        await emitir('inventario.saldo_inicial', {
          productoId,
          ubicacion,
          cantidadBase: qty,
          costoUnitario:
            costoCentavos !== null && costoCentavos > 0
              ? unitCostFromTotal(costoCentavos, fromUnits(1))
              : 0,
        });
      }

      setGuardados((g) => [{ nombre: nombre.trim(), precio: p1, costo: costoCentavos, cantidad }, ...g]);
      limpiar(Boolean(codigo.trim()));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('producto.noSePudoGuardar'));
    } finally {
      setOcupado(false);
    }
  }

  // Enter saves from anywhere in the row; Escape clears it.
  function alTeclado(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      void guardar();
    }
    if (e.key === 'Escape') limpiar(e.target === codigoRef.current);
  }

  useEffect(() => {
    nombreRef.current?.focus();
  }, []);

  if (puedePrecios && activas.length === 0) {
    return (
      <Pagina titulo={t('carga.titulo')} volverA="/productos">
        <p className="text-sm text-slate-400">{t('carga.sinListas')}</p>
      </Pagina>
    );
  }

  return (
    <Pagina
      titulo={t('carga.titulo')}
      subtitulo={t('carga.cargadosSesion', { n: guardados.length })}
      volverA="/productos"
      ancho="amplio"
    >
      {/* Batch settings: these apply to every row, so they are set once and left alone. */}
      <section className="tarjeta mb-4 grid grid-cols-2 gap-4 md:grid-cols-5">
        <CampoCategoria value={categoria} onChange={setCategoria} />

        <Selector
          etiqueta={t('producto.unidad')}
          value={unidadBase}
          onChange={(e) => setUnidadBase(e.target.value)}
        >
          {BASES.map((u) => (
            <option key={u.code} value={u.code}>
              {etiquetaUnidad(u.code, u.label)}
            </option>
          ))}
        </Selector>

        {puedePrecios && (
          <>
            <Campo
              etiqueta={t('carga.margenPublico')}
              inputMode="decimal"
              className="tabular"
              value={margenPublico}
              onChange={(e) => setMargenPublico(e.target.value)}
            />
            <Campo
              etiqueta={t('carga.margenMayorista')}
              inputMode="decimal"
              className="tabular"
              value={margenMayorista}
              onChange={(e) => setMargenMayorista(e.target.value)}
            />
          </>
        )}
      </section>
      {!puedePrecios && (
        <p className="-mt-2 mb-4 text-sm text-slate-400" data-carga-precios-del-dueno>
          {t('producto.preciosDelDueno')}
        </p>
      )}

      {yaVende && (
        <p className="mb-4 rounded-xl bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
          {t(puedePrecios ? 'carga.yaVende' : 'carga.yaVendeSinPrecios')}{' '}
          <Link to="/compras/nueva" className="inline-flex items-center gap-1 font-medium underline underline-offset-2 hover:text-white hover:decoration-2">
            <Ico icono="recibir" className="size-4" />
            {t('compra.nueva')}
          </Link>
        </p>
      )}

      {/* The entry row, pinned at the top so focus never moves around the page. */}
      <section className="tarjeta mb-4" onKeyDown={alTeclado}>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-8">
          <div className="col-span-2">
            <CampoCodigo
              ref={codigoRef}
              valor={codigo}
              onCambio={setCodigo}
              onEnter={() => nombreRef.current?.focus()}
              onEscaneado={() => nombreRef.current?.focus()}
            />
          </div>
          <div className="col-span-2">
            <Campo
              maxLength={120}
              ref={nombreRef}
              etiqueta={t('comun.nombre')}
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder={t('carga.ejemploNombre')}
              autoComplete="off"
            />
          </div>
          {/* What it cost: for working the prices out, and for the owner's opening stock. */}
          {(puedePrecios || conExistencia) && (
            <Campo
              etiqueta={t('carga.costoPor', {
                unidad: etiquetaUnidad(unidadBase, unidadBase),
              })}
              inputMode="decimal"
              className="tabular"
              value={costo}
              onChange={(e) => setCosto(e.target.value)}
              onBlur={puedePrecios ? sugerirPrecios : undefined}
              placeholder="0.00"
            />
          )}
          {conExistencia && (
            <Campo
              etiqueta={t('carga.existencia')}
              inputMode="decimal"
              className="tabular"
              value={cantidad}
              onChange={(e) => setCantidad(e.target.value)}
              placeholder="0"
            />
          )}
          {puedePrecios && (
            <Campo
              etiqueta={publico ? etiquetaListaPrecio(publico) : t('comun.precio')}
              inputMode="decimal"
              className="tabular"
              value={precio1}
              onChange={(e) => setPrecio1(e.target.value)}
              placeholder="0.00"
            />
          )}
          {puedePrecios && mayorista && (
            <Campo
              etiqueta={etiquetaListaPrecio(mayorista)}
              inputMode="decimal"
              className="tabular"
              value={precio2}
              onChange={(e) => setPrecio2(e.target.value)}
              placeholder="0.00"
            />
          )}
        </div>

        <div className="mt-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {costoCentavos !== null && costoCentavos > 0 && p1 !== null && p1 > 0 && (
              <Margen precio={p1} costo={costoCentavos} minimoBp={minimoBp} />
            )}
            {costoCentavos !== null && costoCentavos > 0 && p2 !== null && p2 > 0 && mayorista && (
              <span className="tabular text-xs text-slate-500">
                {etiquetaListaPrecio(mayorista)}: {(margenBp(p2, costoCentavos) / 100).toFixed(1)}%
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-xs text-slate-600 md:inline">
              {t('carga.atajos')}
            </span>
            <button
              type="button"
              className="btn-primario px-6"
              disabled={!nombre.trim() || ocupado}
              onClick={() => void guardar()}
            >
              <Ico icono="guardar" />
              {t('comun.guardar')}
            </button>
          </div>
        </div>

        {error && <p className="mt-3 text-sm text-rose-400">{error}</p>}
      </section>

      {guardados.length > 0 && (
        <section className="tarjeta">
          <h2 className="mb-3 text-sm font-semibold text-slate-400">{t('carga.enEstaSesion')}</h2>
          <ul className="flex flex-col divide-y divide-slate-800">
            {guardados.map((g, i) => (
              <li key={i} className="flex items-baseline justify-between gap-4 py-2 text-sm">
                <span className="truncate">{g.nombre}</span>
                <span className="tabular shrink-0 text-slate-400">
                  {g.cantidad && `${formatQuantity(parseQuantity(g.cantidad) ?? ZERO_QTY)} · `}
                  {g.costo
                    ? `${t('carga.costoResumen', {
                        monto: formatMoney(g.costo, { symbol: false }),
                      })} · `
                    : ''}
                  {g.precio ? formatMoney(g.precio) : t('carga.sinPrecio')}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </Pagina>
  );
}
